import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { EditMaterialSheet } from '../-components/EditMaterialSheet';
import { MaterialsTable } from '../-components/MaterialsTable';
import { adminMaterialsQueryOptions } from '../-hooks/useAdminMaterials';
import { useMaterialsPage } from '../-hooks/useMaterialsPage';

const materialsSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  search: z.string().optional(),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/materials/(pages)/')({
  validateSearch: materialsSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.search !== undefined ? { search: search.search } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject
  // hands the route to the router's error boundary, which hides the table's
  // own "Could not load" banner. Matches the other management lists.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminMaterialsQueryOptions(deps))
      .catch(() => undefined),
  component: MaterialsPage,
});

function MaterialsPage() {
  const page = useMaterialsPage();

  return (
    <PageContainer>
      <MaterialsTable page={page} />
      <EditMaterialSheet page={page} />
    </PageContainer>
  );
}
